import axios from 'axios';
// config
import { HOST_API_KEY } from '../config';
import { isValidToken, setSession } from '../auth/utils';
import { PATH_AUTH } from '../routes/paths';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API_KEY });

const PUBLIC_ENDPOINTS = [
  '/auth/login',
  '/auth/logout',
  '/auth/refresh',
  '/auth/reset-password',
  '/auth/verify-email-reset-password',
  '/send-notification-test',
];

const normalizeUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return new URL(url).pathname;
  return url.startsWith('/') ? url : `/${url}`;
};

const isPublicEndpoint = (config) => {
  const pathname = normalizeUrl(config.url);

  return PUBLIC_ENDPOINTS.some(
    (endpoint) => pathname.startsWith(endpoint)
  );
};

axiosInstance.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('accessToken');

  if (config.skipAuthRefresh) {
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  }

  if (isPublicEndpoint(config) && !config.url.includes('/auth/refresh')) return config;

  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry ||
      (isPublicEndpoint(originalRequest) &&
        !originalRequest.url.includes('/auth/refresh'))
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const res = await axios.post(
        `${HOST_API_KEY}/auth/refresh`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      const newToken = res.data.accessToken;

      setSession(newToken);
      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch (err) {
      processQueue(err, null);
      setSession(null);
      window.location.href = PATH_AUTH.login;
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};
